import React, { useEffect, useState, useCallback } from 'react';

// Material
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';

import { paymentMethods } from '../../models/PaymentMethods';
import { PaymentStates } from '../../models/PaymentStates';
import PaymentDropdown from './paymentDropdown';
import { utils } from '../../utils/utils';

const PaymentMethodModal = ({
    order,
    onChange,
    onClose,
    open,
    business,
    checks,
    banks,
    currencyPrices,
    paymentMethodsHook
}) => {
    const initialState = {
        paymentMethodCurrency: business.defaultCurrency
    }
    const [firstPaymentData, setFirstPaymentData] = useState(initialState);
    const [secondPaymentData, setSecondPaymentData] = useState(initialState);

    const [showSecondPaymentData, setShowSecondPaymentData] = useState(false);
    const [error, setError] = useState('');

    const getActualExpectedAmount = useCallback(() =>
        order.amount - order.interest
        , [order.amount, order.interest])

    useEffect(() => {
        const Map = (payment) => {
            if (!payment) return;

            return {
                ...payment,
                paymentMethodCurrency: payment.currency,
                lastCardDigits: payment.lastCardDigits !== null && payment.lastCardDigits !== undefined
                    ? utils.zeroPad(payment.lastCardDigits, 4)
                    : null
            }
        }

        const payments = order?.payments?.filter(p => p.state === PaymentStates.Initialized);
        if (!!payments && !!payments.length)
            setFirstPaymentData(Map(payments[0]));
        if (payments?.length === 2) {
            setShowSecondPaymentData(true);
            setSecondPaymentData(Map(payments[1]));
        }
    }, [order?.payments])

    useEffect(() => {
        const doValidate = (first, second) => {
            if (showSecondPaymentData) {
                if (first?.installmentHasDiscount || second?.installmentHasDiscount) {
                    setError("Se seleccionó una tarjeta con promoción bancaria. Solo puede usarse para pagar la totalidad de la compra.");
                    return;
                }

                if (validateTwoPaymentCards(first, second)) {
                    setError("Si el pago se realizara en su totalidad con la misma tarjeta, realizarlo una sola vez.");
                    return;
                }

                if (validateTwoPaymentCash(first, second)) {
                    setError("Si el pago será en la totalidad en efectivo, realizarlo una sola vez.");
                    return;
                }

                if (first?.amount < 0 || second?.amount < 0) {
                    setError("Ningun monto puede ser menor a 0");
                    return;
                }

                const expectedAmount = getActualExpectedAmount();

                if (first?.paymentMethodType === paymentMethods.Check &&
                    second?.paymentMethodType === paymentMethods.Check &&
                    (first?.checkAmount + second?.checkAmount) < expectedAmount) {
                    setError("Los cheques deben sumar al menos " + expectedAmount);
                    return;
                }

                const totalAmount = first?.amount + second?.amount;
                if (totalAmount !== expectedAmount) {
                    setError("Los montos deben sumar " + expectedAmount);
                    return;
                }

                if (first?.paymentMethodType === paymentMethods.Check &&
                    second?.paymentMethodType === paymentMethods.Check &&
                    (first?.checkAmount + second?.checkAmount) < expectedAmount) {
                    setError("Los montos deben sumar al menos " + expectedAmount);
                    return;
                }

                if (first?.paymentMethodType === paymentMethods.Check &&
                    second?.paymentMethodType === paymentMethods.Check &&
                    first?.checkId === second?.checkId) {
                    setError("Si los pagos utilizarán el mismo tipo de cheque, sumarlos.");
                    return;
                }

                if ((!!first?.checkAmount && first.checkAmount < first?.amount) ||
                    (!!second?.checkAmount && second.checkAmount < second?.amount)) {
                    setError("El importe de un cheque no puede ser menor al importe del medio de pago.");
                    return;
                }
            } else if (firstPaymentData?.paymentMethodType === paymentMethods.Check &&
                !showSecondPaymentData &&
                !!firstPaymentData?.checkAmount &&
                firstPaymentData?.checkAmount < getActualExpectedAmount()) {
                setError('El monto ingresado para cheques no puede ser menor al total de la venta');
                return;
            }

            setError('');
        }

        doValidate(firstPaymentData, secondPaymentData);
    }, [getActualExpectedAmount, showSecondPaymentData, firstPaymentData, secondPaymentData])

    const onChangeAmount = (name, data, checkAmount) => {
        const amount = parseFloat(data);
        const otherAmount = getActualExpectedAmount() - amount;

        setFirstPaymentData({
            ...firstPaymentData,
            bankId: null,
            cardId: null,
            binNumber: '',
            amount: name === 'first' ? amount : otherAmount
        })

        setSecondPaymentData({
            ...secondPaymentData,
            bankId: null,
            cardId: null,
            binNumber: '',
            amount: name === 'second' ? amount : otherAmount
        })
    }

    const handlerOnAccept = () => {
        const formatPaymentData = (data) => ({
            type: data.paymentMethodType,
            amount: data.amount ? Number(data.amount) : null,
            bankId: data.bankId,
            cardId: data.cardId,
            planId: data.planId,
            installmentId: data.installmentId,
            installmentHasDiscount: data.installmentHasDiscount,
            binNumber: data.binNumber ? parseInt(data.binNumber) : null,
            lastCardDigits: data.lastCardDigits !== null && data.lastCardDigits !== undefined ? parseInt(data.lastCardDigits) : null,
            checkId: data.checkId,
            checkAmount: data.checkAmount,
            paymentMethodCurrency: data.paymentMethodCurrency
        });

        if (!onChange)
            return;

        let paymentMethodData = [];

        if (showSecondPaymentData) {
            paymentMethodData = [
                formatPaymentData(firstPaymentData),
                formatPaymentData(secondPaymentData)
            ];
        } else {
            const paymentData = formatPaymentData(firstPaymentData);
            paymentData.amount = getActualExpectedAmount();
            paymentMethodData = [paymentData];
        }

        onChange({ paymentMethodData });

        if (onClose)
            onClose();
    }

    const validateTwoPaymentCards = (first, second) =>
        first?.binNumber &&
        second?.binNumber &&
        first?.binNumber === second?.binNumber &&
        first?.lastCardDigits === second?.lastCardDigits

    const validateTwoPaymentCash = (first, second) =>
        first?.paymentMethodType === paymentMethods.Cash &&
        second?.paymentMethodType === paymentMethods.Cash &&
        first?.paymentMethodCurrency === second?.paymentMethodCurrency

    const isPaymentsValid = () => {
        const isCardType = (type) =>
            type === paymentMethods.Card || type === paymentMethods.Posnet;

        const isCardValid = (data) =>
            data &&
            isCardType(data.paymentMethodType) &&
            data.binNumber &&
            data.binNumber.toString().length === 6 &&
            data.lastCardDigits &&
            data.lastCardDigits.toString().length === 4 &&
            data.installmentId &&
            data.installmentId > 0 &&
            data.cardId &&
            data.bankId &&
            data.planId

        if (!firstPaymentData?.paymentMethodType)
            return false;

        if (firstPaymentData.paymentMethodType === paymentMethods.Check &&
            (!firstPaymentData?.checkAmount || !firstPaymentData.checkId))
            return false;

        if (isCardType(firstPaymentData.paymentMethodType) && !isCardValid(firstPaymentData))
            return false;

        if (showSecondPaymentData) {
            if (!secondPaymentData?.paymentMethodType)
                return false;

            if (secondPaymentData.paymentMethodType === paymentMethods.Check &&
                (!secondPaymentData?.checkAmount || !secondPaymentData.checkId))
                return false;

            if (isCardType(secondPaymentData.paymentMethodType) && !isCardValid(secondPaymentData))
                return false;
        }

        return !error;
    }

    const CanSelectSecondPayment = () =>
        order && order.payments
            .filter(p => p.state !== PaymentStates.Rejected)
            .filter(p => p.state !== PaymentStates.Cancelled)
            .filter(p => p.state !== PaymentStates.Error)
            .length < 2 &&
            !order.payments.some(p => p.state === PaymentStates.Approved);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth={showSecondPaymentData ? 'md' : 'sm'}
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Método de pago
            </DialogTitle>
            <DialogContent style={{ minWidth: 430 }}>
                <DialogContentText id="alert-dialog-description">
                    {`Selecciona ${!showSecondPaymentData ? 'el método' : 'los métodos'} de pago a utilizar.`}
                </DialogContentText>
                <Grid container spacing={2}>
                    <Grid size={showSecondPaymentData ? 6 : 12}>
                        <PaymentDropdown
                            orderId={order?.orderId}
                            payment={firstPaymentData}
                            onChangeAmount={(data) => onChangeAmount('first', data)}
                            currency={order.currency}
                            onChange={setFirstPaymentData}
                            showAmount={showSecondPaymentData}
                            business={business}
                            checks={checks}
                            banks={banks}
                            currencyPrices={currencyPrices}
                            paymentMethodsHook={paymentMethodsHook}
                        />
                    </Grid>
                    {
                        showSecondPaymentData &&
                        <Grid size={6}>
                            <PaymentDropdown
                                orderId={order?.orderId}
                                payment={secondPaymentData}
                                onChangeAmount={(data) => onChangeAmount('second', data)}
                                currency={order.currency}
                                onChange={setSecondPaymentData}
                                showAmount={showSecondPaymentData}
                                business={business}
                                checks={checks}
                                banks={banks}
                                currencyPrices={currencyPrices}
                                paymentMethodsHook={paymentMethodsHook}
                            />
                        </Grid>
                    }
                </Grid>
                {error &&
                    <DialogContentText style={{ paddingTop: '20px' }}>
                        {error}
                    </DialogContentText>

                }
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="primary"
                >
                    Cancelar
                </Button>
                {
                    !showSecondPaymentData && CanSelectSecondPayment() &&
                    <Button
                        onClick={() => {
                            setFirstPaymentData(initialState);
                            setShowSecondPaymentData(true);
                        }}
                        color="primary"
                        variant='outlined'
                        title='Agregar medio de pago'
                    >
                        +1
                    </Button>
                }
                {
                    showSecondPaymentData &&
                    <Button
                        onClick={() => {
                            setFirstPaymentData(initialState);
                            setShowSecondPaymentData(false);
                            setSecondPaymentData(initialState);
                        }}
                        color="primary"
                        variant='outlined'
                        title='Borrar medio de pago'
                    >
                        -1
                    </Button>
                }
                <Button
                    onClick={handlerOnAccept}
                    color="primary"
                    variant='outlined'
                    autoFocus
                    disabled={!isPaymentsValid()}
                >
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PaymentMethodModal;